import { Link } from 'react-router-dom'
import { FileText, Image, QrCode, Type, Palette, Clock, Ruler, ArrowRight, Zap, Shield, Globe } from 'lucide-react'
import Card from '../components/Card'

const features = [
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Process files in seconds with our optimized algorithms',
  },
  {
    icon: Shield,
    title: '100% Secure',
    description: 'All processing happens in your browser. No data leaves your device.',
  },
  {
    icon: Globe,
    title: 'No Registration',
    description: 'Start using tools immediately. No signup required.',
  },
]

const toolCategories = [
  {
    id: 'image',
    name: 'Image Tools',
    icon: Image,
    count: 8,
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    gradient: 'from-blue-400 to-blue-600',
  },
  {
    id: 'qr',
    name: 'QR & Barcode',
    icon: QrCode,
    count: 6,
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    gradient: 'from-green-400 to-green-600',
  },
  {
    id: 'text',
    name: 'Text Tools',
    icon: Type,
    count: 8,
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    gradient: 'from-purple-400 to-purple-600',
  },
  {
    id: 'color',
    name: 'Color Tools',
    icon: Palette,
    count: 6,
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600',
    gradient: 'from-pink-400 to-pink-600',
  },
  {
    id: 'time',
    name: 'Time Tools',
    icon: Clock,
    count: 8,
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    gradient: 'from-orange-400 to-orange-600',
  },
  {
    id: 'unit',
    name: 'Unit Converter',
    icon: Ruler,
    count: 8,
    bgColor: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    gradient: 'from-indigo-400 to-indigo-600',
  },
  {
    id: 'pdf',
    name: 'PDF Tools',
    icon: FileText,
    count: 6,
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    gradient: 'from-red-400 to-red-600',
  },
]

export default function Home() {
  return (
    <div className="space-y-4">
      {/* Compact Hero Section */}
      <div className="text-center py-4">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Free Online Tools - PDF, Image, QR Code, Text & Unit Converter
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mx-auto">
          Transform your files, images, and data with our comprehensive suite of free online tools. Access PDF tools for merging, splitting, and compressing PDFs. Use image tools for converting, resizing, and editing images. Generate QR codes and barcodes. Format and analyze text. Convert units, colors, and time. Fast, secure, and completely free. No registration required.
        </p>
      </div>

      {/* Tools Grid */}
      <section aria-label="Free online tools categories">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {toolCategories.map((category) => {
            const Icon = category.icon
            return (
              <Link key={category.id} to={`/tools/${category.id}`} aria-label={`Browse ${category.name} - ${category.count} free online tools`}>
                <Card className="hover:shadow-lg transition-all duration-200 group cursor-pointer h-full border-2 border-gray-200">
                  <Card.Body className="p-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${category.gradient} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm`} aria-hidden="true">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {category.name}
                    </h2>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">{category.count} free tools</span>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" aria-hidden="true" />
                    </div>
                  </Card.Body>
                </Card>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Features Section */}
      <section aria-label="Why choose our free online tools">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <Card key={index} className="text-center">
                <Card.Body className="p-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-2" aria-hidden="true">
                    <Icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-xs text-gray-600">{feature.description}</p>
                </Card.Body>
              </Card>
            )
          })}
        </div>
      </section>
    </div>
  )
}

